import * as React from 'react';
import { useIntl } from 'react-intl';
import {
  FormControl,
  IconButton,
  InputLabel,
  Button,
  colors,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  MenuItem,
  Link,
  Select,
  TextField
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import { Column } from 'material-table';
import AlteredMaterialTable from 'components/MaterialTable';
import { SUPPORTED_LOCALES } from 'components/LanguageSwitcher';
import { MonthlyReportRow } from 'redux/ducks/monthlyReports';
import { MonthlyReportRecipient } from 'redux/ducks/monthlyReportRecipients/types';

export type RecipientsModalProps = {
  open: boolean;
  accountId?: string;
  reportRows: MonthlyReportRow[] | null;
  isSending: boolean;
  isRecipientsMutationPending: boolean;
  onAddRecipient: (
    recipient: Omit<MonthlyReportRecipient, 'id' | 'accountId' | 'userId'>
  ) => Promise<void>;
  onDeleteRecipient: (recipientId: MonthlyReportRecipient['id']) => Promise<void>;
  onSend: () => void;
  onClose: () => void;
};

const recipientsTableOptions = {
  toolbar: false,
  showTitle: false,
  search: false,
  paging: false,
  emptyRowsWhenPaging: false,
  headerStyle: {
    backgroundColor: colors.grey['50']
  },
  rowStyle: {
    borderBottom: `1px solid ${colors.grey['200']}`
  }
};

type RecipientsCellProps = {
  recipients: MonthlyReportRow['recipients'];
  isRecipientsMutationPending: boolean;
  onDeleteRecipient: (recipientId: MonthlyReportRecipient['id']) => void;
};

const RecipientsCell: React.FC<RecipientsCellProps> = ({
  recipients,
  isRecipientsMutationPending,
  onDeleteRecipient
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  if (!recipients?.length) {
    return <>{'-'}</>;
  }

  const firstRecipient = recipients[0];
  const extraRecipientsCount = recipients.length - 1;
  const visibleRecipients = isExpanded ? recipients : [firstRecipient];

  return (
    <div>
      <List
        dense
        disablePadding
        style={isExpanded ? { maxHeight: 120, overflowY: 'auto' } : undefined}
      >
        {visibleRecipients.map((recipient) => (
          <ListItem
            key={recipient.id}
            disableGutters
            style={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <span title={recipient.email}>{recipient.email}</span>
            <IconButton
              edge='end'
              aria-label='Delete recipient'
              onClick={() => {
                onDeleteRecipient(recipient.id);
              }}
              disabled={isRecipientsMutationPending}
            >
              <DeleteIcon fontSize='small' />
            </IconButton>
          </ListItem>
        ))}
      </List>
      {extraRecipientsCount > 0 ? (
        <Link
          component='button'
          variant='body2'
          onClick={() => setIsExpanded((prev) => !prev)}
          style={{ marginTop: 4 }}
        >
          {isExpanded ? 'Show less' : `+${extraRecipientsCount} more`}
        </Link>
      ) : null}
    </div>
  );
};

const RecipientsModal: React.FC<RecipientsModalProps> = ({
  open,
  accountId,
  reportRows,
  isSending,
  isRecipientsMutationPending,
  onAddRecipient,
  onDeleteRecipient,
  onSend,
  onClose
}) => {
  const intl = useIntl();
  const [draftEmail, setDraftEmail] = React.useState('');
  const [draftLanguage, setDraftLanguage] = React.useState('en');

  React.useEffect(() => {
    setDraftEmail('');
    setDraftLanguage('en');
  }, [open, reportRows]);

  const handleAddRecipientSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!accountId || !draftEmail.trim() || !draftLanguage) {
      return;
    }

    await onAddRecipient({
      email: draftEmail.trim(),
      language: draftLanguage
    });
    setDraftEmail('');
  };

  const recipientsColumns = React.useMemo<Column<MonthlyReportRow>[]>(
    () => [
      {
        title: intl.formatMessage({ id: 'language' }),
        field: 'language',
        editable: 'never'
      },
      {
        title: intl.formatMessage({ id: 'file' }),
        field: 'report',
        editable: 'never',
        render: (row) =>
          row.report?.fileUrl ? (
            <Link href={row.report.fileUrl} target='_blank' rel='noopener noreferrer'>
              {intl.formatMessage({ id: 'base.view', defaultMessage: 'View' })}
            </Link>
          ) : (
            '-'
          )
      },
      {
        title: intl.formatMessage({ id: 'admin.monthlyReports.status' }),
        field: 'report',
        editable: 'never',
        render: (row) => row.report?.status || '-'
      },
      {
        title: intl.formatMessage({ id: 'error' }),
        field: 'report',
        editable: 'never',
        render: (row) => row.report?.errorMessage || '-'
      },
      {
        title: intl.formatMessage({ id: 'recipientsMultiple' }),
        field: 'recipients',
        editable: 'never',
        render: (row) => (
          <RecipientsCell
            recipients={row.recipients}
            isRecipientsMutationPending={isRecipientsMutationPending}
            onDeleteRecipient={(recipientId) => {
              void onDeleteRecipient(recipientId);
            }}
          />
        )
      }
    ],
    [intl, isRecipientsMutationPending, onDeleteRecipient]
  );

  const hasRows = !!reportRows?.length;

  const content = (
    <>
      <form
        onSubmit={(event) => {
          void handleAddRecipientSubmit(event);
        }}
        style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 16 }}
      >
        <TextField
          label={intl.formatMessage({ id: 'emailAddress', defaultMessage: 'Email address' })}
          type='email'
          required
          fullWidth
          value={draftEmail}
          onChange={(event) => setDraftEmail(event.target.value)}
          disabled={!accountId || isRecipientsMutationPending}
        />
        <FormControl style={{ minWidth: 140 }}>
          <InputLabel htmlFor='admin-recipient-language'>
            {intl.formatMessage({ id: 'language', defaultMessage: 'Language' })}
          </InputLabel>
          <Select
            inputProps={{ id: 'admin-recipient-language', name: 'admin-recipient-language' }}
            value={draftLanguage}
            required
            onChange={(event) => setDraftLanguage(event.target.value as string)}
            disabled={!accountId || isRecipientsMutationPending}
          >
            {SUPPORTED_LOCALES.map((locale) => (
              <MenuItem key={locale} value={locale}>
                {intl.formatMessage({ id: `languages.${locale}` })}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          color='primary'
          variant='contained'
          type='submit'
          startIcon={<AddIcon />}
          disabled={!accountId || isRecipientsMutationPending || !draftEmail.trim()}
        >
          {intl.formatMessage({ id: 'admin.monthlyReports.recipients.add', defaultMessage: 'Add' })}
        </Button>
      </form>

      {hasRows ? (
        <AlteredMaterialTable
          columns={recipientsColumns}
          data={reportRows}
          options={recipientsTableOptions}
          style={{
            border: `1px solid ${colors.grey['200']}`,
            overflow: 'hidden'
          }}
        />
      ) : null}
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={isRecipientsMutationPending ? undefined : onClose}
      fullWidth
      maxWidth='md'
    >
      <DialogTitle>
        {intl.formatMessage({ id: 'recipientsMultiple', defaultMessage: 'Recipient(s)' })}
      </DialogTitle>
      <DialogContent>
        {content}
        {hasRows ? (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
            <Button color='primary' variant='outlined' onClick={onSend} disabled={isSending}>
              {intl.formatMessage({
                id: 'admin.monthlyReports.sendEmails',
                defaultMessage: 'Send emails'
              })}
            </Button>
          </div>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isRecipientsMutationPending}>
          {intl.formatMessage({ id: 'base.ok', defaultMessage: 'Ok' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecipientsModal;
